import { useState, useEffect, type ReactNode, useRef } from 'react';
import { Layer, Marker, Popup, Source, useMap, type MarkerProps, type PopupInstance } from '@vis.gl/react-maplibre';
import { Circle, MapPin, X } from 'lucide-react';
import { circle as turfCircle } from '@turf/circle';

export function TaskMarker({
	position,
	radius,
	autoPan = false,
	unlocked = false,
	infoWindow,
}: {
	position: { lat: number; lng: number };
	radius?: number;
	autoPan?: boolean;
	unlocked?: boolean;
	infoWindow?: ReactNode;
}) {
	const timeOutSeconds = 7;
	const { current: map } = useMap();
	const [popupInfo, setPopupInfo] = useState(false);
	const popupRef = useRef<PopupInstance | null>(null);

	useEffect(() => {
		if (map && autoPan) {
			map.setCenter(position);
		}
	}, [map, position, autoPan]);

	return (
		<>
			{popupInfo && infoWindow && (
				<Popup
					ref={popupRef}
					anchor="bottom"
					longitude={position.lng}
					latitude={position.lat}
					closeButton={false}
					closeOnClick={false}
					onClose={() => setPopupInfo(false)}
					onOpen={() => {
						setTimeout(() => {
							if (popupInfo) setPopupInfo(false);
						}, timeOutSeconds * 1000);
					}}
				>
					<div className="flex justify-end">
						{/* popup x button */}
						<button
							className=""
							onClick={() => {
								//Remove popup (trigger close)
								popupRef.current?.remove();
								setPopupInfo(false);
							}}
						>
							<X className="size-5 text-gray-950" />
						</button>
					</div>
					{infoWindow}
				</Popup>
			)}
			<Pin
				position={position}
				unlocked={unlocked}
				anchor="bottom"
				onClick={(e) => {
					e.originalEvent.stopPropagation();
					setPopupInfo(true);
				}}
			/>
			{radius && <MapRadius unlocked={unlocked} position={position} radius={radius} />}
		</>
	);
}

export function Pin(
	props: Omit<MarkerProps, 'latitude' | 'longitude'> & {
		position: { lat: number; lng: number };
		unlocked: boolean;
	},
) {
	const fillColor = props.unlocked ? '#4ca147' : '#ef4444';
	const outlineColor = props.unlocked ? '#008000' : '#dc2626';

	return (
		<Marker {...props} latitude={props.position.lat} longitude={props.position.lng}>
			<div className="relative flex items-center justify-center">
				<MapPin color={outlineColor} fill={fillColor} size={55} strokeWidth={1.5} />
				<Circle
					className="absolute"
					fill={'oklch(0.208 0.042 265.755)'}
					color={outlineColor}
					size={20}
					style={{ top: '44%', left: '50%', transform: 'translate(-50%, -50%)' }}
				/>
			</div>
		</Marker>
	);
}

export function MapRadius(props: { position: { lat: number; lng: number }; radius: number; unlocked: boolean }) {
	const shape = turfCircle([props.position.lng, props.position.lat], props.radius, { steps: 48, units: 'meters' });
	// Generate Id based on coords (+radius)
	const elemId = `radius${props.position.lat}-${props.position.lng}-${props.radius}`;

	return (
		<Source id={elemId} type="geojson" data={shape}>
			{/* Fill */}
			<Layer
				id={elemId + '-fill'}
				type="fill"
				paint={{
					'fill-color': props.unlocked ? 'green' : '#e879f9',
					'fill-opacity': 0.2,
				}}
			/>
			{/* Outline */}
			<Layer
				id={elemId + '-outline'}
				type="line"
				paint={{
					'line-color': props.unlocked ? 'green' : '#e879f9',
					'line-width': 2,
				}}
			/>
		</Source>
	);
}
